import { z } from "zod";
import { buildAreaData } from "@/components/ui/area-chart/ChatAreaChartSpec";
import type { ChartConfig } from "@/components/ui/ChartTooltip";
import LineChartView from "@/components/ui/line-chart/LineChartView";
import { chartHexColorSchema } from "@/lib/utils/chartUtils";

export const ChatLineChartSpec = {
  props: z.object({
    title: z.string().nullable().optional(),
    data: z.array(z.record(z.string(), z.unknown())),
    xKey: z.string(),
    yKey: z.string(),
    aggregate: z.enum(["sum", "count", "avg"]).nullable().optional(),
    height: z.number().nullable().optional(),
    color: chartHexColorSchema.nullable().optional(),
  }),
  description:
    'Line chart visualization. Props: title (string or null), data (array of records), xKey (string), yKey (string), aggregate ("sum" | "count" | "avg" or null), height (number or null), color (optional #RGB or #RRGGBB series colour). Use { "$state": "/path" } to bind read-only data.',
  example: {
    title: "Coverage volume over time",
    data: [
      { day: "2026-03-01", articles: 124 },
      { day: "2026-03-02", articles: 188 },
      { day: "2026-03-03", articles: 143 },
      { day: "2026-03-04", articles: 201 },
    ],
    xKey: "day",
    yKey: "articles",
    aggregate: null,
    height: 280,
  },
} as const;

/** Parses props and renders {@link LineChartView}. */
export const ChatLineChartFromProps = ({
  props,
}: {
  props: Record<string, unknown>;
}) => {
  const parsed = ChatLineChartSpec.props.safeParse(props);
  if (!parsed.success) return null;

  const typed = parsed.data;
  const chartData = buildAreaData({
    data: typed.data,
    xKey: typed.xKey,
    yKey: typed.yKey,
    aggregate: typed.aggregate ?? null,
  });

  const chartConfig = {
    [typed.yKey]: {
      label:
        typed.yKey.length > 0
          ? `${typed.yKey.charAt(0).toUpperCase()}${typed.yKey.slice(1)}`
          : typed.yKey,
      color: typed.color ?? "var(--chart-1)",
    },
  } satisfies ChartConfig;

  return (
    <LineChartView
      title={typed.title ?? null}
      data={chartData}
      chartConfig={chartConfig}
      xAxisKey={typed.xKey}
      dataKey={typed.yKey}
      height={typed.height ?? null}
      dot={false}
    />
  );
};
